import { ConvexError } from "convex/values";

/**
 * Read the machine-readable error code out of a thrown Convex error.
 *
 * WHY THIS EXISTS (2026-07-30 prod incident): Convex only propagates payload
 * content to the client for `ConvexError` — a plain `throw new Error(CODE)`
 * has its message REDACTED on production deployments and reaches the browser
 * as `[Request ID: …] Server Error`. Client mappers that string-matched
 * `error.message` therefore worked in dev and silently degraded every coded
 * failure to the generic transport copy in prod (a studio app that was over
 * the per-file cap rendered "We couldn't open that app" instead of the honest
 * "too large" copy — `studioActions:readApp` logged `APP_FILE_TOO_LARGE` the
 * whole time).
 *
 * Server surfaces MUST throw `ConvexError(CODE)` for any code that drives
 * user-facing copy (backend-doctrine §10.2). This helper reads `.data` first
 * and falls back to the message so plain-`Error` throw sites (dev, and the
 * modules not yet swept) keep working.
 */
export function errorCodeText(error: unknown): string {
  if (error instanceof ConvexError) return String(error.data);
  // Structural fallback: a ConvexError that crossed a module-instance boundary
  // fails `instanceof` but still carries `.data`.
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as { data: unknown }).data === "string"
  ) {
    return (error as { data: string }).data;
  }
  return error instanceof Error ? error.message : String(error);
}
